<script setup lang="ts">
// ================================
// IMPORTS
// ================================
import { ref, computed, inject } from "vue"
import { useI18n } from "vue-i18n"
import { useFetchWrapper } from "@core/composables"
import type { Services } from "@services"

// ================================
// INTERNATIONALIZATION
// ================================
const { t } = useI18n({
    messages: {
        fr: {
            title: "Mot de passe oublié ?",
            subtitle:
                "Saisissez votre e-mail pour recevoir un lien de réinitialisation.",
            email: "E-mail",
            submit: "Envoyer",
            successMessage: "Un e-mail de réinitialisation a été envoyé.",
            firstLogin: "C’est votre première connexion ?",
            firstLoginLink: "Première connexion",
            alreadyAccount: "Vous avez déjà un compte ?",
            login: "Se connecter",
        },
    },
})

// ================================
// PROPS & EMITS
// ================================
defineEmits<{ (e: "click:login"): void }>()

// ================================
// COMPOSABLES
// ================================
const { $sendResetPasswordEmail } = inject<Services>("services")!
// ================================
// STATE
// ================================
/** E-mail saisi pour la réinitialisation */
const username = ref("")
/** Message d'erreur affiché */
const errorMessage = ref("")
/** Message de succès après envoi du mail */
const successMessage = ref("")

/** Wrapper d'appel envoi mail reset password */
const resetFetch = useFetchWrapper(async () => {
    errorMessage.value = ""
    successMessage.value = ""
    await $sendResetPasswordEmail(username.value)
    successMessage.value = t("successMessage")
})

// ================================
// COMPUTED
// ================================
/** Liens du footer (première connexion, connexion) */
const getFooterLinks = computed(() => [
    { text: t("alreadyAccount"), label: t("login"), link_to: "/login" },
    {
        text: t("firstLogin"),
        label: t("firstLoginLink"),
        link_to: "/login?view=first-login",
    },
])

// ================================
// INTERACTIONS
// ================================
/** Soumet le formulaire et envoie l'e-mail de réinitialisation. */
function onSubmit(): void {
    if (!username.value) return
    resetFetch.fetch()
}
</script>

<template>
    <BaseCard>
        <template #title>
            <FormAuthHeader :title="t('title')" :subtitle="t('subtitle')" />
        </template>
        <template #content>
            <form
                class="flex flex-col gap-6 w-full py-6"
                @submit.prevent="onSubmit"
            >
                <BaseMessage v-if="errorMessage" severity="error">{{
                    errorMessage
                }}</BaseMessage>
                <BaseMessage v-if="successMessage" severity="success">{{
                    successMessage
                }}</BaseMessage>
                <BaseInputWrapper
                    input-id="auth-forget-password-email"
                    :label="t('email')"
                >
                    <BaseInputText
                        id="auth-forget-password-email"
                        v-model="username"
                        type="email"
                        class="w-full"
                        required
                    />
                </BaseInputWrapper>
                <BaseButton
                    type="submit"
                    :label="t('submit')"
                    :loading="resetFetch.isFetching.value"
                    :disabled="!username"
                    class="w-full"
                />
            </form>
            <FooterLogin :links="getFooterLinks" />
        </template>
    </BaseCard>
</template>
